/**
 * Endereço público do cliente: calendar.dreamvision.com.br/@javalibarber
 *
 * O handle é o nome na URL, sem token. A senha de 4 dígitos mora no cadastro
 * do cliente (hash Argon2), não no link. Quem adivinha o handle ainda precisa
 * do PIN — e o PIN troca a qualquer momento, invalidando sessões antigas.
 */

import { validationError } from "../errors.ts";

export const CLIENT_HANDLE_PATTERN = /^[a-z0-9]{3,48}$/;
export const CLIENT_PIN_PATTERN = /^\d{4}$/;

export const RESERVED_CLIENT_HANDLES = new Set([
  "admin",
  "api",
  "app",
  "assets",
  "auth",
  "c",
  "calendario",
  "clientes",
  "compartilhamento",
  "configuracoes",
  "favicon",
  "health",
  "hermes",
  "login",
  "me",
  "portal",
  "privacidade",
  "projetos",
  "publicacoes",
  "ready",
  "realtime",
  "static",
  "view",
  "www",
]);

export function handleFromName(name: string): string {
  const compact = name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .slice(0, 48);

  if (compact.length >= 3 && !RESERVED_CLIENT_HANDLES.has(compact)) return compact;
  const padded = `cliente${compact}`.slice(0, 48);
  return padded.length >= 3 ? padded : "cliente";
}

export function assertValidHandle(handle: string): string {
  const normalized = handle.trim().toLowerCase();
  if (!CLIENT_HANDLE_PATTERN.test(normalized)) {
    throw validationError(
      "O endereço público aceita 3 a 48 letras minúsculas ou números, sem espaços",
      {
        handle: normalized,
      },
    );
  }
  if (RESERVED_CLIENT_HANDLES.has(normalized)) {
    throw validationError("Este endereço está reservado");
  }
  return normalized;
}

export function assertValidPin(pin: string): string {
  if (!CLIENT_PIN_PATTERN.test(pin)) {
    throw validationError("A senha do cliente deve ter exatamente 4 dígitos");
  }
  return pin;
}

export function buildClientViewUrl(baseUrl: string, handle: string): string {
  const normalized = baseUrl.replace(/\/+$/, "");
  return `${normalized}/@${handle}`;
}

/**
 * Lê o handle de um pathname como `/@javalibarber` ou `/%40javalibarber`.
 *
 * Independente do router: se o TanStack não casar `/@{$handle}`, a página
 * ainda consegue abrir a visão do cliente a partir da URL do browser.
 */
export function parseClientHandleFromPath(pathname: string): string | null {
  const pathOnly = pathname.split(/[?#]/)[0] ?? "";
  let decoded = pathOnly;
  try {
    decoded = decodeURIComponent(pathOnly);
  } catch {
    decoded = pathOnly;
  }
  const match = /^\/+@([a-z0-9]{3,48})\/?$/i.exec(decoded);
  if (match === null) return null;
  const captured = match[1];
  if (captured === undefined) return null;
  const handle = captured.toLowerCase();
  if (RESERVED_CLIENT_HANDLES.has(handle)) return null;
  return handle;
}

/**
 * Gera um handle ainda não usado.
 *
 * `taken` é sincrônico de propósito: o chamador consulta o banco e passa o
 * predicado, para esta função permanecer pura e testável.
 */
export function uniquifyHandle(base: string, taken: (handle: string) => boolean): string {
  const normalized = assertValidHandle(base);
  if (!taken(normalized)) return normalized;

  for (let n = 2; n <= 99; n += 1) {
    const suffix = String(n);
    const candidate = `${normalized.slice(0, 48 - suffix.length)}${suffix}`;
    if (
      CLIENT_HANDLE_PATTERN.test(candidate) &&
      !RESERVED_CLIENT_HANDLES.has(candidate) &&
      !taken(candidate)
    ) {
      return candidate;
    }
  }

  throw validationError("Não foi possível gerar um endereço público único");
}
